import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { VersionRange } from '../../models/core/version-range.js';
import {
  cloneList, cloneMap, isSameData, isSameList, isSameMap, mapToData,
} from '../../models/dto/data-model-utils.js';
import {
  DataModelBuilder, SerializationContext, SerializedData,
} from '../../models/dto/data-model.js';
import { EventCategory } from '../../models/serializable/event-category.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class EventPriority implements ICloneable, IDataComparable {
  private constructor(
    public readonly isFeatured: boolean,
    public readonly featuredScore: number,
  ) {}

  static create(args: {
    isFeatured: boolean,
    featuredScore: number,
  }): EventPriority {
    return new EventPriority(
        args.isFeatured,
        args.featuredScore,
    );
  }

  static empty(): EventPriority {
    return this.create({
      isFeatured: false,
      featuredScore: 0,
    });
  }

  static fromMap(map: SerializedData): EventPriority {
    return this.create({
      isFeatured: MapUtils.getBoolean(map, 'isFeatured'),
      featuredScore: MapUtils.getDouble(map, 'featuredScore'),
    });
  }

  toMap(): SerializedData {
    return {
      isFeatured: this.isFeatured,
      featuredScore: this.featuredScore,
    };
  }

  copyWith({
    isFeatured = this.isFeatured,
    featuredScore = this.featuredScore,
  }: {
    isFeatured?: boolean,
    featuredScore?: number,
  } = {}): this {
    return EventPriority.create({
      isFeatured: isFeatured,
      featuredScore: featuredScore,
    }) as this;
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.isFeatured === this.isFeatured &&
        other.featuredScore === this.featuredScore;
  }
}

type PartnerAppUniqueId = string;

export class EventClassificationComponent extends Component {
  static readonly TYPE_ID: string = 'dd3cd833d7a740c78f07780a47f9dc64';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly defaultPriority: EventPriority,
    public readonly partnerPriorities: Map<PartnerAppUniqueId, EventPriority>,
    public readonly classification: EventCategory[],
  ) {
    super();
  }

  get $typeId(): string {
    return EventClassificationComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(EventClassificationComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  isFeatured(partnerAppId?: PartnerAppUniqueId | null): boolean {
    return this.getPartnerPriorityOrDefault(partnerAppId).isFeatured;
  }

  featuredScore(partnerAppId?: PartnerAppUniqueId | null): number {
    return this.getPartnerPriorityOrDefault(partnerAppId).featuredScore;
  }

  getPartnerPriorityOrDefault(partnerAppId?: PartnerAppUniqueId | null): EventPriority {
    let result: EventPriority | null = null;
    if(partnerAppId) {
      result = this.partnerPriorities.get(partnerAppId) ?? null;
    }

    result ??= this.defaultPriority;
    return result;
  }

  static create(args: {
    defaultPriority: EventPriority,
    partnerPriorities: Map<PartnerAppUniqueId, EventPriority>,
    classification: EventCategory[],
  }): EventClassificationComponent {
    return new EventClassificationComponent(
        args.defaultPriority,
        args.partnerPriorities,
        args.classification,
    );
  }

  static empty(): EventClassificationComponent {
    return this.create({
      defaultPriority: EventPriority.create({
        isFeatured: false,
        featuredScore: 0,
      }),
      partnerPriorities: new Map<PartnerAppUniqueId, EventPriority>(),
      classification: [],
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventClassificationComponent {
    return this.create({
      defaultPriority: EventPriority.fromMap(MapUtils.getRecord(map, 'defaultPriority')),
      partnerPriorities: MapUtils.getMap<PartnerAppUniqueId, EventPriority>(
          map,
          'partnerPriorities',
          (k, v) => ({
            key: k,
            value: EventPriority.fromMap(JSONUtils.decodeRecord(v)),
          })),
      classification: MapUtils.getList<EventCategory>(
          map,
          'classification',
          (item) => toEnumFromString(JSONUtils.decodeString(item), EventCategory),
      ),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      defaultPriority: this.defaultPriority.toMap(),
      partnerPriorities: mapToData(this.partnerPriorities, (k, v) => ({
        key: k,
        value: v.toMap(),
      })),
      classification: this.classification.map((item) => fromEnumAsString(item)),
    };
  }

  copyWith({
    defaultPriority = this.defaultPriority,
    partnerPriorities = cloneMap(this.partnerPriorities, (v) => v.clone()),
    classification = cloneList(this.classification),
  }: {
    defaultPriority?: EventPriority,
    partnerPriorities?: Map<PartnerAppUniqueId, EventPriority>,
    classification?: EventCategory[],
  } = {}): this {
    return EventClassificationComponent.create({
      defaultPriority: defaultPriority,
      partnerPriorities: partnerPriorities,
      classification: classification,
    }) as this;
  }

  copyWithPartnerPriority(
      partnerAppId: PartnerAppUniqueId,
      updater: (p: EventPriority | null) => EventPriority | null,
  ): this {
    const existingPriority = this.partnerPriorities.get(partnerAppId) ?? null;
    const updatedPriority = updater(existingPriority);

    const newPartnerPriorities = cloneMap(this.partnerPriorities, (v) => v.clone());

    if(updatedPriority) {
      newPartnerPriorities.set(partnerAppId, updatedPriority);
    }
    else {
      newPartnerPriorities.delete(partnerAppId);
    }

    return this.copyWith({
      partnerPriorities: newPartnerPriorities,
    });
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.defaultPriority, this.defaultPriority) &&
        isSameMap(other.partnerPriorities, this.partnerPriorities, isSameData) &&
        isSameList(other.classification, this.classification);
  }
}

export class EventClassificationComponentBuilder extends DataModelBuilder<EventClassificationComponent> {
  build(context: SerializationContext, map: SerializedData): EventClassificationComponent {
    return EventClassificationComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    this.migrateEnumList(map, 'classification', EventCategory);

    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(EventClassificationComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(EventClassificationComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - Move `isFeatured` of type {@link boolean} and `featuredScore` of type {@link number} to `defaultPriority` of type {@link EventPriority}.
     *  - Add `partnerPriorities` of type {@link Map<PartnerAppUniqueId, EventPriority>}.
     */
    if(version < 2) {
      const isFeatured = MapUtils.getBoolean(map, 'isFeatured', false);
      const featuredScore = MapUtils.getDouble(map, 'featuredScore', 0);

      const defaultPriority = EventPriority.create({
        isFeatured: isFeatured,
        featuredScore: featuredScore,
      });

      MapUtils.setIfNullOrUndefined(map, 'defaultPriority', defaultPriority.toMap());
      MapUtils.setIfNullOrUndefined(map, 'partnerPriorities', {});

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(context: SerializationContext, model: EventClassificationComponent, map: SerializedData): SerializedData {
    let version = model.$schemaVersion.to;

    if(version >= 2) {
      // Partner priorities are not backwards compatible.
      if(model.partnerPriorities.size > 0) {
        return map;
      }

      MapUtils.set(map, 'isFeatured', model.defaultPriority.isFeatured);
      MapUtils.set(map, 'featuredScore', model.defaultPriority.featuredScore);

      delete map['defaultPriority'];
      delete map['partnerPriorities'];

      version = 1;
      map['$schema_version'] = VersionRange.encodedExactly(version);
    }

    return map;
  }
}
